import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Mail, Phone, Instagram, Facebook, Send, MessageCircle } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

export default function Contact() {
  const { settings, t } = useSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  React.useEffect(() => { document.title = "Contact – Cheap4Teach"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="contact-page">
      <h1 className="text-4xl sm:text-5xl font-['Fredoka'] font-bold">{t("contact.title", "Get in Touch")}</h1>
      <p className="c4t-desc text-base sm:text-lg mt-2">{t("contact.subtitle", "Questions, custom orders, or just want to say hi? We'd love to hear from you.")}</p>

      <div className="grid md:grid-cols-2 gap-8 mt-10">
        <form onSubmit={submit} className="c4t-card space-y-4" data-testid="contact-form">
          <div>
            <label className="text-sm font-bold">Your Name</label>
            <input className="c4t-input mt-1" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
          </div>
          <div>
            <label className="text-sm font-bold">Email</label>
            <input type="email" className="c4t-input mt-1" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
          </div>
          <div>
            <label className="text-sm font-bold">Message</label>
            <textarea rows={5} className="c4t-input mt-1" required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
          </div>
          <button type="submit" disabled={sending} className="c4t-btn c4t-btn-pink w-full" data-testid="contact-submit-btn">
            <Send className="w-4 h-4" /> {sending ? "Sending..." : "Send Message"}
          </button>
        </form>

        <div className="space-y-5">
          <div className="c4t-card flex items-center gap-4">
            <span className="rounded-full p-3 bg-[#FFC500] border-[3px] border-black"><Mail className="w-5 h-5" strokeWidth={2.5}/></span>
            <div>
              <p className="text-sm c4t-desc">Email</p>
              <p className="font-['Fredoka'] font-bold text-lg break-all">{settings?.contact_email}</p>
            </div>
          </div>
          <div className="c4t-card flex items-center gap-4">
            <span className="rounded-full p-3 bg-[#00E0AA] border-[3px] border-black"><Phone className="w-5 h-5" strokeWidth={2.5}/></span>
            <div>
              <p className="text-sm c4t-desc">WhatsApp</p>
              <p className="font-['Fredoka'] font-bold text-lg">{settings?.whatsapp}</p>
            </div>
          </div>
          <div className="c4t-card">
            <p className="font-['Fredoka'] font-bold text-lg mb-3 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-[#FF1F87]"/> {t("contact.follow_label", "Follow Us")}</p>
            <div className="flex flex-wrap gap-3">
              <a href={settings?.instagram || "#"} target="_blank" rel="noreferrer" className="c4t-pill" data-testid="social-instagram"><Instagram className="w-4 h-4"/> Instagram</a>
              <a href={settings?.facebook || "#"} target="_blank" rel="noreferrer" className="c4t-pill" data-testid="social-facebook"><Facebook className="w-4 h-4"/> Facebook</a>
              <a href={settings?.tiktok || "#"} target="_blank" rel="noreferrer" className="c4t-pill" data-testid="social-tiktok">TikTok</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
